<template>
  <div
    :class="[
      'user-profile-card rounded-xl shadow-sm overflow-hidden transition-colors duration-300',
      cardAppearance,
      compact ? 'inline-block' : ''
    ]"
  >
    <template v-if="isAuthenticated">
      <template v-if="compact">
        <button
          type="button"
          class="flex items-center justify-center w-full p-2 text-left bg-transparent border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:focus-visible:ring-emerald-600 hover:bg-green-100/50 dark:hover:bg-slate-700/50 transition-colors rounded-xl"
          :title="userProfile.fullName ?? undefined"
          @click="handleNavigateToProfile"
        >
          <div
            class="w-10 h-10 bg-green-500 dark:bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm"
          >
            {{ initials }}
          </div>
        </button>
      </template>
      <template v-else>
        <button
          type="button"
          class="w-full flex items-center gap-3 p-3 text-left bg-transparent border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:focus-visible:ring-emerald-600"
          @click="handleNavigateToProfile"
        >
          <div
            class="w-8 h-8 bg-green-500 dark:bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0 shadow-sm"
          >
            {{ initials }}
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ userProfile.fullName }}</p>
            <p v-if="userProfile.email" class="text-sm text-gray-600 dark:text-slate-400 truncate">
              {{ userProfile.email }}
            </p>
            <p v-if="userProfile.phoneNumber" class="text-sm text-gray-600 dark:text-slate-400 truncate">
              {{ userProfile.phoneNumber }}
            </p>
          </div>
        </button>

        <button
          class="group w-full flex items-center justify-between gap-3 px-4 py-3 text-emerald-700 dark:text-emerald-400 font-semibold bg-white/80 dark:bg-slate-800/50 border-t border-green-200/60 dark:border-slate-700 transition-all hover:bg-emerald-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:focus-visible:ring-emerald-600"
          @click="handleSignOut"
        >
          <span class="flex items-center gap-3">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 transition-transform group-hover:scale-105"
            >
              <IconLogOut class="w-4 h-4" />
            </span>
            <span v-translate="'signOut'" class="text-sm tracking-wide"></span>
          </span>
          <span class="flex items-center gap-2">
            <span v-if="showTimer" class="font-mono text-xs text-emerald-600/70 dark:text-emerald-400/70 tabular-nums">
              {{ formattedTime }}
            </span>
            <IconChevronRight class="w-4 h-4 text-emerald-400 transition-transform group-hover:translate-x-0.5" />
          </span>
        </button>
      </template>
    </template>

    <template v-else>
      <template v-if="compact">
        <button
          type="button"
          class="flex items-center justify-center p-3 rounded-xl bg-transparent hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          :title="translate('nav.signIn')"
          @click="handleGuestSignIn"
        >
          <IconUser class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
        </button>
      </template>
      <template v-else>
        <div class="flex items-start gap-3 p-4">
          <span
            class="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300"
          >
            <IconUser class="w-5 h-5" />
          </span>
          <div class="space-y-2">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ translate("common.guestTitle") }}
            </p>
            <p class="text-sm text-gray-600 dark:text-slate-400">
              {{ translate("common.guestDescription") }}
            </p>
          </div>
        </div>
        <div class="px-4 pb-4">
          <button
            type="button"
            class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:bg-emerald-500 dark:hover:bg-emerald-400"
            @click="handleGuestSignIn"
          >
            <IconArrowRight class="w-4 h-4" />
            <span>{{ translate("nav.signIn") }}</span>
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import type { Ref } from "vue";

import { useAppConfig } from "@core/composables/data/useAppConfig";
import { useNavigation } from "@core/composables/state/useNavigation";
import { useProfile } from "@core/composables/state/useProfile";
import { useRouteContext } from "@core/composables/state/useRouteContext";
import IconArrowRight from "@core/icons/IconArrowRight.vue";
import IconChevronRight from "@core/icons/IconChevronRight.vue";
import IconLogOut from "@core/icons/IconLogOut.vue";
import IconUser from "@core/icons/IconUser.vue";
import { signOut } from "@core/utils/auth";

import { useTranslate } from "@common/composables/useTranslate";

import { buildAuthRedirectUrl } from "@/router/utils";

interface Props {
  compact?: boolean;
}

withDefaults(defineProps<Props>(), {
  compact: false
});

const { config } = useAppConfig();
const { profile: userProfile } = useProfile();
const route = useRouteContext();
const { navigateTo } = useNavigation();
const translate = useTranslate();
const isAuthenticated = computed(() => config.isAuthenticated);
const isImpersonated = computed(() => config.isImpersonatedUser);

const remainingTimeMs = inject<Ref<number> | null>("autoSignOutTimer", null);

const showTimer = computed(() => {
  return isAuthenticated.value && !isImpersonated.value && remainingTimeMs !== null;
});

const formattedTime = computed(() => {
  if (!remainingTimeMs) {
    return "02:00:00";
  }

  const totalSeconds = Math.floor(remainingTimeMs.value / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num: number) => String(num).padStart(2, "0");

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
});

const cardAppearance = computed(() => {
  if (isImpersonated.value && isAuthenticated.value) {
    return "bg-red-50 dark:bg-red-950/20 border-2 border-red-500 dark:border-red-600";
  }
  if (isAuthenticated.value) {
    return "bg-gradient-to-br from-green-50 dark:from-slate-700 to-emerald-50 dark:to-slate-800 border-2 border-green-200/50 dark:border-slate-600";
  }
  return "bg-white dark:bg-slate-900 border-2 border-emerald-200/60 dark:border-emerald-400/40";
});

const confirmSignOut = inject<(options?: { scope?: "single" | "all" }) => Promise<boolean>>(
  "confirmSignOut",
  async () => {
    await signOut();
    return true;
  }
);

const initials = computed(() => {
  const fullName = userProfile.value?.fullName ?? "";
  const nameParts = fullName?.split(" ") ?? [];
  if (nameParts.length >= 2) {
    return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  }
  return fullName?.substring(0, 2).toUpperCase() ?? "";
});

const handleSignOut = async () => {
  await confirmSignOut();
};

const handleNavigateToProfile = async () => {
  await navigateTo("profile");
};

const handleGuestSignIn = async () => {
  const redirectUrl = buildAuthRedirectUrl("/dashboard", route.fullPath);
  await navigateTo("sign-in", {}, { query: { RedirectUrl: redirectUrl } });
};
</script>

<style scoped>
.user-profile-card {
  transition: all 0.2s ease;
}
</style>
